import React from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Icon from 'src/components/ui/Icon';
import Button from 'src/components/ui/Button';
import Avatar from 'src/components/ui/Avatar';
import Typography from 'src/components/ui/Typography';
import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import type { Medium, UserProfileQuery } from 'src/types/__generated__/graphql';
import theme from 'src/utils/theme';
import { styledComponent, styledTag } from 'src/utils/styled';

const StyledCard = styledComponent(Card)`
    background-color: ${theme.palette.primary.main};
`;

StyledCard.defaultProps = {
    variant: 'outlined',
};

const StyledCardContent = styledComponent(CardContent)`
    padding: 1rem 1.5rem;
    &.MuiCardContent-root:last-child {
        padding-bottom: 1rem;
    }

    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        padding: 1rem;
    }
`;

const StyledText = styledComponent(Typography)`
    color: ${colours.grey.G50};
`;

const StyledAvatar = styledComponent(Avatar)`
    position: absolute;
    top: 0;
    left: 0;
`;

const IconContainer = styledTag('div')`
    border-radius: 2rem;
    width: 2rem;
    height: 2rem;
    background-color: ${colours.tertiary.main};
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 0;
    right: 0;
`;

interface Props {
    recommendations: UserProfileQuery['recommendations'];
    recommendationsCount: number;
    openRecommendationModal: () => void;
}

const RecommendationsSection: React.FC<Props> = ({
    recommendationsCount,
    recommendations,
    openRecommendationModal,
}) => {
    const { t } = useTranslation(['profile', 'common']);

    const hasRecommendations = recommendations && recommendations.length > 0;

    return (
        <Box>
            <StyledCard>
                <StyledCardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" pr={2}>
                            <Box
                                display="flex"
                                mr="0.75rem"
                                height={hasRecommendations ? '2.5rem' : '2rem'}
                                width={hasRecommendations ? '3rem' : '2rem'}
                                position="relative"
                            >
                                {hasRecommendations && (
                                    <StyledAvatar
                                        key={recommendations[0].recommendingUser.id}
                                        image={recommendations[0].recommendingUser.image as Medium}
                                        alt={recommendations[0].recommendingUser.fullName}
                                        fallbackText={recommendations[0].recommendingUser.initials}
                                    />
                                )}
                                <IconContainer>
                                    <Icon size="1.25rem" color={colours.white.main} name="ThumbsUpOutlined" />
                                </IconContainer>
                            </Box>
                            <StyledText>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t('profile:recommendationCount', {
                                            count: recommendationsCount,
                                            plurial: recommendationsCount > 1 ? 's' : '',
                                        }),
                                    }}
                                />
                            </StyledText>
                        </Box>
                        <Button variant="contained" color="white" onClick={openRecommendationModal}>
                            {t('profile:seeRecommendations')}
                        </Button>
                    </Box>
                </StyledCardContent>
            </StyledCard>
        </Box>
    );
};

export default RecommendationsSection;
